import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const NotificationCenter = ({ notifications = [], onNotificationClick, onMarkAsRead }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, currentLanguage } = useLanguage();

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const priorityConfig = {
    high: {
      icon: 'AlertCircle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    medium: {
      icon: 'Info',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    low: {
      icon: 'Bell',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    }
  };

  const labels = {
    fr: {
      title: 'Notifications',
      empty: 'Aucune notification',
      markAllRead: 'Tout marquer comme lu'
    },
    ar: {
      title: 'الإشعارات',
      empty: 'لا توجد إشعارات',
      markAllRead: 'وضع علامة مقروءة على الكل'
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification) => {
    setIsOpen(false);
    onNotificationClick?.(notification);
    if (!notification?.read) {
      onMarkAsRead?.(notification?.id);
    }
  };

  const handleMarkAllRead = () => {
    const unreadIds = notifications?.filter(n => !n?.read)?.map(n => n?.id);
    unreadIds?.forEach(id => onMarkAsRead?.(id));
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffMs = now - new Date(timestamp);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.now');
    if (diffMins < 60) return `${diffMins} ${t('notifications.minutesAgo')}`;
    if (diffHours < 24) return `${diffHours} ${t('notifications.hoursAgo')}`;
    return `${diffDays} ${t('notifications.daysAgo')}`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-md bg-card hover:bg-muted transition-smooth shadow-warm hover:shadow-warm-md"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('notifications.title')}
      >
        <Icon name="Bell" size={20} className="text-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs font-medium rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-md shadow-warm-lg z-[100] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-border bg-card">
            <h3 className="font-semibold text-foreground">
              {t('notifications.title')}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="caption text-primary hover:text-primary/80 transition-smooth"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications?.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {t('notifications.empty')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => {
                  const config = priorityConfig?.[notification?.priority] || priorityConfig?.low;
                  return (
                    <button
                      key={notification?.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`
                        w-full p-4 text-left hover:bg-muted transition-smooth
                        ${!notification?.read ? 'bg-primary/5' : ''}
                      `}
                    >
                      <div className="flex gap-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config?.bgColor} flex items-center justify-center`}>
                          <Icon name={config?.icon} size={18} className={config?.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className={`text-sm font-medium ${!notification?.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {notification?.title}
                            </p>
                            <span className="caption text-muted-foreground flex-shrink-0">
                              {formatTimeAgo(notification?.timestamp)}
                            </span>
                          </div>
                          <p className="caption text-muted-foreground line-clamp-2">
                            {notification?.message}
                          </p>
                          {!notification?.read && (
                            <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;